<template>
  <AlertDialog v-model:open="isOpen">
    <AlertDialogTrigger as-child>
      <slot />
    </AlertDialogTrigger>

    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>
          {{
            isLeavingMyself
              ? t("settings.removeMember.leave.title")
              : t("settings.removeMember.remove.title")
          }}
        </AlertDialogTitle>
        <AlertDialogDescription>
          <div class="space-y-2">
            <p>
              {{
                isLeavingMyself
                  ? t("settings.removeMember.leave.description")
                  : t("settings.removeMember.remove.description", {
                      name: member?.name || member?.email
                    })
              }}
            </p>

            <!-- Warning if member is the last admin -->
            <div
              v-if="isLastAdmin"
              class="p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800"
            >
              <div class="flex items-start space-x-2">
                <TriangleAlert class="h-4 w-4 mt-0.5 text-amber-600" />
                <div class="text-sm">
                  <p class="font-medium">
                    {{ t("settings.removeMember.lastAdminWarning.title") }}
                  </p>
                  <p>
                    {{
                      t("settings.removeMember.lastAdminWarning.description")
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter>
        <AlertDialogCancel>
          {{ t("settings.removeMember.cancelButton") }}
        </AlertDialogCancel>
        <AlertDialogAction
          :disabled="isLastAdmin || isLoading"
          class="bg-red-600 hover:bg-red-700 focus:ring-red-600"
          @click="handleConfirm"
        >
          <LoadingIndicator v-if="isLoading" class="mr-2" />
          <LogOut v-else-if="isLeavingMyself" class="w-4 h-4 mr-2" />
          <UserMinus v-else class="w-4 h-4 mr-2" />
          {{
            isLeavingMyself
              ? t("settings.removeMember.leave.confirmButton")
              : t("settings.removeMember.remove.confirmButton")
          }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<script setup lang="ts">
import { LogOut, TriangleAlert, UserMinus } from "lucide-vue-next";
import type { ApiOrganizationMember } from "~/server/api/organizations/[organizationId]/members/index.get";

interface Props {
  member: ApiOrganizationMember | null;
  isLeavingMyself: boolean;
  isLastAdmin: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  confirmed: [member: ApiOrganizationMember];
}>();

const isOpen = ref(false);
const isLoading = ref(false);
const { t } = useI18n();

const handleConfirm = async () => {
  if (!props.member || props.isLastAdmin) return;

  isLoading.value = true;
  try {
    emit("confirmed", props.member);
  } finally {
    isLoading.value = false;
    isOpen.value = false;
  }
};

// Reset loading state when dialog closes
watch(isOpen, (newValue) => {
  if (!newValue) {
    isLoading.value = false;
  }
});
</script>
